import React, { useState, useEffect } from 'react';
import { mxElementAliases, parseJsonMessage, downloadMxMessageAsXml, generateXmlFromJson } from '../../utils/mxMessageUtils';

interface MxMessageModalProps {
  message: string;
  paymentId: string;
  onClose: () => void;
}

// Tree node component
interface TreeNodeProps {
  name: string;
  value: any;
  level: number;
}

const TreeNode: React.FC<TreeNodeProps> = ({ name, value, level }) => {
  const [isExpanded, setIsExpanded] = useState(level < 1);
  
  // Get human-readable label
  const getLabel = (key: string): string => {
    return mxElementAliases[key] || key;
  };

  // Is this node expandable?
  const isExpandable = typeof value === 'object' && value !== null;

  // Handle click to expand/collapse
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExpandable) {
      setIsExpanded(!isExpanded);
    }
  };

  // Generate indentation based on level
  const indent = `${level * 20}px`;

  return (
    <div>
      <div 
        style={{ 
          paddingLeft: indent,
          cursor: isExpandable ? 'pointer' : 'default',
          padding: '6px 8px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: level % 2 === 0 ? '#f9f9f9' : 'white',
          transition: 'background-color 0.2s',
        }}
        onClick={handleToggle}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = level % 2 === 0 ? '#f9f9f9' : 'white';
        }}
      >
        {isExpandable && (
          <span style={{ marginRight: '8px' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        <span style={{ fontWeight: 600 }}>{getLabel(name)}</span>
        {!isExpandable && (
          <span style={{ marginLeft: '8px' }}>: {String(value)}</span>
        )}
      </div>

      {isExpanded && isExpandable && (
        <div>
          {Array.isArray(value) ? (
            // Handle arrays
            value.map((item, index) => (
              <div key={index}>
                {typeof item === 'object' && item !== null ? (
                  // If array item is an object, render it as a nested tree
                  Object.entries(item).map(([key, val]) => (
                    <TreeNode 
                      key={key} 
                      name={key} 
                      value={val} 
                      level={level + 1} 
                    />
                  ))
                ) : (
                  // If array item is a primitive, render it directly
                  <div style={{ 
                    paddingLeft: `${(level + 1) * 20}px`,
                    padding: '6px 8px',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                    {String(item)}
                  </div>
                )}
              </div>
            ))
          ) : (
            // Handle objects
            Object.entries(value).map(([key, val]) => (
              <TreeNode 
                key={key} 
                name={key} 
                value={val} 
                level={level + 1} 
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const MxMessageModal: React.FC<MxMessageModalProps> = ({ message, paymentId, onClose }) => {
  const [parsedMessage, setParsedMessage] = useState<any>(null);
  const [xmlContent, setXmlContent] = useState<string>('');

  // Parse the message on mount
  useEffect(() => {
    try {
      const parsed = parseJsonMessage(message);
      setParsedMessage(parsed);
      
      // Generate XML
      if (parsed) {
        const xml = generateXmlFromJson(parsed);
        setXmlContent(xml);
      }
    } catch (error) {
      console.error('Error parsing message:', error);
    }
  }, [message]);

  // Handle download XML
  const handleDownloadXml = () => {
    downloadMxMessageAsXml(xmlContent, paymentId);
  };

  // Close modal when clicking overlay or pressing ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Prevent clicking inside the modal from closing it
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          maxWidth: '90%',
          width: '800px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
        onClick={handleModalClick}
      >
        {/* Modal Header */}
        <div 
          style={{
            padding: '16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 id="modal-title" style={{ margin: 0 }}>MX Message Details</h3>
          <button 
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px',
            }}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        {/* Modal Content */}
        <div 
          style={{
            padding: '16px',
            overflowY: 'auto',
            maxHeight: 'calc(90vh - 120px)',
          }}
        >
          {parsedMessage ? (
            <div>
              {Object.entries(parsedMessage).map(([key, value]) => (
                <TreeNode 
                  key={key} 
                  name={key} 
                  value={value} 
                  level={0} 
                />
              ))}
            </div>
          ) : (
            <div>Unable to parse message</div>
          )}
        </div>
        
        {/* Modal Footer */}
        <div 
          style={{
            padding: '16px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <button 
            style={{
              backgroundColor: '#003366',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
            onClick={handleDownloadXml}
          >
            Download XML
          </button>
          <button 
            style={{
              backgroundColor: '#f0f0f0',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MxMessageModal; 